import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "sonner";
import Loader from "../components/Loader";
import { useCreateOrderMutation } from "../slices/ordersApiSlice.js";
import {
  useGetBillingAddressByUserIdQuery,
  useSaveBillingAddressMutation,
} from "../slices/usersApiSlice.js";
import { countryCityData } from "../constants.js";
import { clearCartItems, saveShippingAddress } from "../slices/cartSlice.js";
import { useGetExchangeRatesQuery } from "../slices/exchangeSlice.js";
import { formatPrice } from "../utils/formatPrice.js";

function PlaceOrderScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [useSavedAddress, setUseSavedAddress] = useState(false);
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);

  const [createOrder, { isLoading, error }] = useCreateOrderMutation();

  const userId = useSelector((state) => state.auth.userInfo?._id);
  const { data: savedAddress, isSuccess } = useGetBillingAddressByUserIdQuery(userId, {
    skip: !userId,
  });
  const [saveBillingAddress] = useSaveBillingAddressMutation();

  const { data: exchangeData } = useGetExchangeRatesQuery();
  const userInfo = useSelector((state) => state.auth.userInfo);
  const preferredCurrency = userInfo?.preferredCurrency || "USD";
  const freeItemsCount = cart.cartItems
    .filter((item) => Boolean(item.isFree) || Number(item.price) <= 0)
    .reduce((acc, item) => acc + Number(item.qty || 0), 0);
  const paidSubtotal = cart.cartItems
    .filter((item) => !Boolean(item.isFree) && Number(item.price) > 0)
    .reduce((acc, item) => acc + Number(item.qty || 0) * Number(item.price || 0), 0);
  const chargeableTotal = paidSubtotal;

  useEffect(() => {
    if (cart.cartItems.length === 0) {
      navigate("/cart");
    }
  }, [cart.cartItems, navigate]);

  async function placeOrderHandler() {
    if (!address || !city || !postalCode || !country) {
      toast.error("Please complete billing address");
      return;
    }

    if (!acceptedPolicies) {
      toast.error("Please accept the Privacy Policy and Terms & Conditions");
      return;
    }

    const shippingData = { address, city, postalCode, country };
    dispatch(saveShippingAddress(shippingData));

    if (saveForFuture) {
      try {
        await saveBillingAddress({ address, city, postalCode, country });
      } catch {
        toast.error("Couldn't save billing address");
      }
    }

    try {
      const res = await createOrder({
        orderItems: cart.cartItems.map((item) => ({
          qty: item.qty,
          product: item._id,
        })),
        shippingAddress: shippingData,
        paymentMethod: "Stripe",
        itemsPrice: cart.itemsPrice,
        shippingPrice: 0,
        taxPrice: 0,
        totalPrice: cart.itemsPrice,
      }).unwrap();

      navigate(`/order/${res._id}`);
      setTimeout(() => dispatch(clearCartItems()), 0);
    } catch (createErr) {
      console.error("Order Creation Error:", createErr);
      toast.error(
        createErr?.data?.message || createErr.message || "Something went wrong"
      );
    }
  }

  return (
    <div className="space-y-6">
      <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700">Sign In</span>
        <span className="rounded-full bg-sky-100 px-2 py-0.5 text-sky-700">Place Order</span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <section className="space-y-5 lg:col-span-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
                  Checkout
                </p>
                <h2 className="text-xl font-semibold text-slate-900">Billing Address</h2>
                <p className="mt-1 text-sm leading-6 text-slate-500">
                  We use this address for your purchase record and invoice details.
                </p>
              </div>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                Step 1
              </div>
            </div>

            {isSuccess && savedAddress?.address && (
              <label className="mb-4 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-sky-600"
                  checked={useSavedAddress}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setUseSavedAddress(checked);
                    if (checked) {
                      setAddress(savedAddress.address || "");
                      setCity(savedAddress.city || "");
                      setPostalCode(savedAddress.postalCode || "");
                      setCountry(savedAddress.country || "");
                    } else {
                      setAddress("");
                      setCity("");
                      setPostalCode("");
                      setCountry("");
                    }
                  }}
                />
                <span className="text-sm text-slate-700">Use saved billing address</span>
              </label>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="address" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Address
                </label>
                <input
                  id="address"
                  type="text"
                  placeholder="Enter address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="country" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Country
                  </label>
                  <select
                    id="country"
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      setCity("");
                    }}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
                  >
                    <option value="">-- Select Country --</option>
                    {Object.keys(countryCityData).map((countryName) => (
                      <option key={countryName} value={countryName}>
                        {countryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="city" className="mb-1.5 block text-sm font-medium text-slate-700">
                    City
                  </label>
                  <select
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    disabled={!country || !countryCityData[country]}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    {!country || !countryCityData[country] ? (
                      <option>Please choose country first</option>
                    ) : (
                      <>
                        <option value="">-- Select City --</option>
                        {countryCityData[country].map((cityName) => (
                          <option key={cityName} value={cityName}>
                            {cityName}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="postalCode" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Postal Code
                </label>
                <input
                  id="postalCode"
                  type="text"
                  placeholder="Enter postal code"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
                />
              </div>
            </div>

            <label className="mt-4 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-sky-600"
                checked={saveForFuture}
                onChange={(e) => setSaveForFuture(e.target.checked)}
              />
              <span className="text-sm text-slate-700">
                Save this billing address for future purchases
              </span>
            </label>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-xl font-semibold text-slate-900">Order Items</h2>
              <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                {cart.cartItems.length} item{cart.cartItems.length === 1 ? "" : "s"}
              </div>
            </div>
            {cart.cartItems.length === 0 ? (
              <p className="m-0 text-sm text-slate-600">Your cart is empty</p>
            ) : (
              <div className="space-y-3">
                {cart.cartItems.map((item, index) => (
                  <article
                    key={index}
                    className="grid grid-cols-1 items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[56px_minmax(0,1fr)_220px]"
                  >
                    <img
                      src={item.images[0]}
                      alt={item.name}
                      className="h-14 w-14 rounded-md border border-slate-200 object-cover"
                    />
                    <Link
                      to={`/product/${item.product || item._id}`}
                      className="truncate text-sm font-semibold text-slate-800 no-underline hover:text-sky-700"
                    >
                      {item.name}
                    </Link>
                    <div className="text-sm text-slate-700">
                      {item.qty} x{" "}
                      {Boolean(item.isFree) || Number(item.price) <= 0
                        ? "Free"
                        : formatPrice(item.price, preferredCurrency, exchangeData)}{" "}
                      ={" "}
                      <strong>
                        {Boolean(item.isFree) || Number(item.price) <= 0
                          ? "Free"
                          : formatPrice(item.qty * item.price, preferredCurrency, exchangeData)}
                      </strong>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <aside className="lg:col-span-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
                Review
              </p>
              <h2 className="text-xl font-semibold text-slate-900">Order Summary</h2>
            </div>
            <div className="space-y-2 text-sm text-slate-700">
              <div className="flex items-center justify-between gap-4">
                <span>Total:</span>
                <strong>
                  {chargeableTotal <= 0
                    ? "Free"
                    : formatPrice(chargeableTotal, preferredCurrency, exchangeData)}
                </strong>
              </div>
            </div>

            <div className="mt-4 border-t border-slate-200 pt-4 text-xs leading-5 text-slate-500">
              {freeItemsCount > 0 && (
                <p className="mb-2">
                  Includes {freeItemsCount} free item{freeItemsCount === 1 ? "" : "s"} in this order.
                </p>
              )}
              {chargeableTotal <= 0
                ? "This order contains free items only, so it will skip payment and move straight to the claim step."
                : "You will review and pay this order securely with Stripe on the next step."}
            </div>
            {error && (
              <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">
                {error?.data?.message || error?.error || "Failed to create order"}
              </div>
            )}

            <label className="mt-4 flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm leading-6 text-slate-700">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-sky-600"
                checked={acceptedPolicies}
                onChange={(e) => setAcceptedPolicies(e.target.checked)}
              />
              <span>
                I have read and agree to the{" "}
                <Link
                  to="/privacy"
                  className="font-semibold text-sky-700 no-underline hover:text-sky-800"
                >
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link
                  to="/terms"
                  className="font-semibold text-sky-700 no-underline hover:text-sky-800"
                >
                  Terms & Conditions
                </Link>
                .
              </span>
            </label>

            <button
              type="button"
              disabled={cart.cartItems.length === 0 || !acceptedPolicies}
              onClick={placeOrderHandler}
              className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {chargeableTotal <= 0 ? "Create Free Order" : "Place Order"}
            </button>

            {isLoading && (
              <div className="mt-3">
                <Loader />
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export default PlaceOrderScreen;

